/**
 * Composer attachment model — one entry per file the user attached, tracked
 * from the moment it is picked until the backend returns its uuid.
 */

export type AttachmentStatus = 'uploading' | 'ready' | 'failed';

export interface ComposerAttachment {
  /** Local id — stable across the upload, unlike the backend uuid. */
  id: string;
  name: string;
  size: number;
  status: AttachmentStatus;
  /** Backend attachment uuid, set once the upload succeeds. */
  uuid?: string;
  /** Where the upload was stored, when the response says. */
  blobPath?: string;
  container?: string;
  /** Failure reason, shown on the pill's tooltip. */
  error?: string;
}

/**
 * An attachment as it hangs off a SENT turn.
 *
 * Deliberately not `ComposerAttachment`: once sent there is no upload status
 * and nothing to remove — only what the file was.
 */
export interface TurnAttachment {
  /** Backend attachment uuid, when we know it. */
  uuid?: string;
  name: string;
  size?: number;
  mimeType?: string;
  /**
   * The file's OWN storage path.
   *
   * This is what makes an upload openable in its own right. Matching an
   * attachment to an uploaded-document citation only works when the engine
   * actually cited it — a file dropped by retrieval, or one attached to a
   * question the engine answered from elsewhere, has no citation and used to
   * have no way to open at all.
   */
  blobPath?: string;
  container?: string;
}

let counter = 0;

/** Local id for a freshly picked file. */
export function newAttachmentId(): string {
  counter += 1;
  return `att-${counter}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Pull the attachment uuid out of an upload response.
 *
 * The endpoint has returned the record at a few different depths, so accept
 * each of them rather than silently producing an attachment with no uuid.
 */
export function attachmentUuidFrom(response: any): string | undefined {
  const candidate =
    response?.uuid ||
    response?.attachment_uuid ||
    response?.attachment?.uuid ||
    response?.data?.uuid ||
    response?.data?.attachment?.uuid;
  return typeof candidate === 'string' && candidate ? candidate : undefined;
}

/**
 * Where an upload was stored, if the response says.
 *
 * The endpoint has not consistently returned this; an absent path simply
 * means the file becomes openable after a reload, when the messages endpoint
 * supplies it.
 */
export function attachmentBlobFrom(response: any): { blobPath?: string; container?: string } {
  const node = response?.attachment ?? response?.data?.attachment ?? response?.data ?? response;
  const blobPath = [node?.blob_path, node?.path, node?.file_path]
    .map((v: any) => (typeof v === 'string' ? v.trim() : ''))
    .find((v: string) => v !== '');
  const container = [node?.container, node?.container_name]
    .map((v: any) => (typeof v === 'string' ? v.trim() : ''))
    .find((v: string) => v !== '');
  return {
    ...(blobPath ? { blobPath } : {}),
    ...(container ? { container } : {}),
  };
}

/** The uuids to send with a query — only successfully uploaded files. */
export function readyAttachmentUuids(attachments: ComposerAttachment[]): string[] {
  return attachments
    .filter((a) => a.status === 'ready' && a.uuid)
    .map((a) => a.uuid as string);
}

/** True while any attachment is still uploading — send should wait. */
export function hasPendingUploads(attachments: ComposerAttachment[]): boolean {
  return attachments.some((a) => a.status === 'uploading');
}

/* ------------------------------------------------------------------ */
/*  Attachments on a turn                                              */
/* ------------------------------------------------------------------ */

/** The attachments that actually made it onto the query we just sent. */
export function sentAttachments(attachments: ComposerAttachment[]): TurnAttachment[] {
  return attachments
    .filter((a) => a.status === 'ready' && a.uuid)
    .map((a) => ({
      uuid: a.uuid,
      name: a.name,
      size: a.size,
      // The upload response may name where it was stored; without it the file
      // is openable only after a reload brings the path back.
      ...(a.blobPath ? { blobPath: a.blobPath } : {}),
      ...(a.container ? { container: a.container } : {}),
    }));
}

/** One backend attachment record, whatever it calls its fields. */
function toTurnAttachment(raw: any): TurnAttachment | null {
  if (!raw) return null;
  if (typeof raw === 'string') return raw.trim() ? { name: raw.trim() } : null;
  if (typeof raw !== 'object') return null;

  const name = [raw.file_name, raw.filename, raw.name, raw.original_name, raw.title]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find((v) => v !== '');
  const uuid = [raw.uuid, raw.attachment_uuid, raw.id]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find((v) => v !== '');

  // A record with neither a name nor an id is not something we can show.
  if (!name && !uuid) return null;

  const blobPath = [raw.blob_path, raw.blobPath, raw.path, raw.file_path, raw.storage_path]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find((v) => v !== '');
  const container = [raw.container, raw.container_name, raw.blob_container]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find((v) => v !== '');

  const size = [raw.size, raw.file_size, raw.bytes].find((v) => typeof v === 'number' && v >= 0);
  const mimeType = [raw.mime_type, raw.mimeType, raw.content_type, raw.type]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find((v) => v !== '');

  return {
    ...(uuid ? { uuid } : {}),
    name: name || uuid || '',
    ...(typeof size === 'number' ? { size } : {}),
    ...(mimeType ? { mimeType } : {}),
    ...(blobPath ? { blobPath } : {}),
    ...(container ? { container } : {}),
  };
}

/**
 * The attachments a stored message carries.
 *
 * Attachments are POSTed to their own endpoint, so whether the message row
 * echoes them back is the backend's call — several field names are accepted
 * and an absent list simply means "no chip", never an error.
 */
export function attachmentsFromMessage(item: any): TurnAttachment[] {
  const raw = item?.attachments ?? item?.files ?? item?.attachment ?? item?.uploaded_documents;
  // A single record is accepted, but a bare string in this field is not an
  // attachment — it would render a chip named after whatever the value was.
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [];
  return list.map(toTurnAttachment).filter((a): a is TurnAttachment => a !== null);
}

/** Is this citation the ingested copy of something the user uploaded? */
export function isUploadedSource(citation: any): boolean {
  const type = String(citation?.source_type || '').trim().toLowerCase();
  const label = String(citation?.document_type || '').trim().toLowerCase();
  return type === 'uploaded_document' || type === 'attachment' || label === 'uploaded document';
}

const normalise = (value: string): string =>
  String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * The citation that IS this attachment, if the answer cited it.
 *
 * That citation is the only openable handle we have: the upload endpoint is
 * write-only, so an attachment's uuid cannot be streamed back. Matching is by
 * uuid where the engine echoes one, then by file name — and a citation with
 * no `blob_path` is not offered, since there would be nothing to stream.
 */
export function uploadedCitationFor(
  attachment: TurnAttachment,
  citations: any[] | undefined
): any | null {
  const candidates = (citations || []).filter(
    (c) => isUploadedSource(c) && String(c?.blob_path || '').trim() !== ''
  );
  if (candidates.length === 0) return null;

  if (attachment.uuid) {
    const byUuid = candidates.find(
      (c) => c.document_uuid === attachment.uuid || c.attachment_uuid === attachment.uuid
    );
    if (byUuid) return byUuid;
  }

  const name = normalise(attachment.name);
  if (!name) return null;
  return (
    candidates.find((c) => normalise(c.file_name) === name || normalise(c.title) === name) || null
  );
}

/**
 * Every document uploaded in a chat, oldest first.
 *
 * Aggregated across the whole transcript rather than the last turn, so the
 * list is the chat's document set — which is how a user thinks about files
 * they attached three questions ago. Deduped by attachment uuid where there
 * is one, otherwise by name, because the same file re-sent on a follow-up is
 * still one document.
 */
export function chatDocuments(
  turns: Array<{ kind: string; attachments?: TurnAttachment[] }>
): TurnAttachment[] {
  const seen = new Set<string>();
  const documents: TurnAttachment[] = [];

  turns.forEach((turn) => {
    if (turn.kind !== 'user') return;
    (turn.attachments || []).forEach((attachment) => {
      const key = attachment.uuid || attachment.name.trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      documents.push(attachment);
    });
  });

  return documents;
}

/**
 * Every citation in the chat that could stand in for an uploaded document.
 *
 * The upload endpoint is write-only, so opening a file means opening the
 * engine's ingested copy of it — and that copy may have been cited by ANY
 * turn, not just the one the file rode in on.
 */
export function chatUploadedCitations(
  turns: Array<{ kind: string; response?: { citations?: any[] } }>
): any[] {
  return turns.flatMap((turn) =>
    turn.kind === 'assistant' ? (turn.response?.citations || []) : []
  );
}

/**
 * How to OPEN an attachment.
 *
 * Its own `blob_path` first: an upload is a stored blob like any other, and
 * `stream-source` decrypts it, so nothing about opening it should depend on
 * whether the engine happened to cite it. The citation match stays as the
 * fallback for a file whose path the backend has not sent us.
 *
 * Returns a citation-SHAPED object because that is the contract the streaming
 * path already speaks (`citationTarget` reads `blob_path` / `container` /
 * `url`); this keeps one open path rather than a second parallel one.
 */
export function attachmentCitation(
  attachment: TurnAttachment,
  citations?: any[]
): any | null {
  const blobPath = String(attachment.blobPath || '').trim();
  if (blobPath) {
    return {
      // Enough for the viewer's header and the streaming call. Not a real
      // engine citation — it has no number and no locator, deliberately.
      id: attachment.uuid || blobPath,
      title: attachment.name,
      file_name: attachment.name,
      blob_path: blobPath,
      container: String(attachment.container || '').trim(),
      url: '',
      source_type: 'uploaded_document',
      provider: 'uploaded_document',
      document_type: 'Uploaded document',
      ...(attachment.mimeType ? { content_type: attachment.mimeType } : {}),
      ...(attachment.uuid ? { document_uuid: attachment.uuid } : {}),
    };
  }
  return uploadedCitationFor(attachment, citations);
}
